import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Phone, Mail, Loader2, Calendar } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const MESSAGE_COOLDOWN_MS = 3000; // 3 seconds between messages
const MAX_MESSAGES_PER_HOUR = 30;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showAppointmentOptions, setShowAppointmentOptions] = useState(false);
  const [contactData, setContactData] = useState({ name: '', email: '', phone: '' });
  const [problemSummary, setProblemSummary] = useState('');
  const [lastMessageTime, setLastMessageTime] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const WHATSAPP_URL = "https://wa.me/4915128958006?text=Hallo,%20ich%20möchte%20gerne%20einen%20Termin%20vereinbaren.";

  const openWhatsApp = async () => {
    const win = window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");
    if (!win) {
      try {
        await navigator.clipboard?.writeText(WHATSAPP_URL);
      } catch {
        // ignore
      }
      toast({
        title: "WhatsApp im Vorschaufenster blockiert",
        description: "Der Link wurde (wenn möglich) kopiert. Bitte in einem neuen Tab öffnen oder im Live-Link testen.",
      });
    }
  };

  // Auto-open after 3 seconds on first visit
  useEffect(() => {
    const hasVisited = sessionStorage.getItem('chatWidgetShown');
    if (!hasVisited && !hasAutoOpened) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        setHasAutoOpened(true);
        sessionStorage.setItem('chatWidgetShown', 'true');
        // Add welcome message
        setMessages([{
          role: 'assistant',
          content: 'Hallo! 👋 Ich bin der KI-Assistent von Klartech Müritz. Wie kann ich Ihnen heute bei Ihrem IT-Problem helfen?'
        }]);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [hasAutoOpened]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    // Rate limiting checks
    const now = Date.now();
    
    // Cooldown check
    if (now - lastMessageTime < MESSAGE_COOLDOWN_MS) {
      toast({
        title: "Bitte warten",
        description: "Bitte warten Sie einen Moment zwischen Nachrichten.",
        variant: "destructive"
      });
      return;
    }

    // Hourly message limit check
    const storedData = sessionStorage.getItem('chatMessageData');
    let hourlyData = storedData ? JSON.parse(storedData) : { count: 0, resetTime: now + 3600000 };
    
    if (now > hourlyData.resetTime) {
      hourlyData = { count: 0, resetTime: now + 3600000 };
    }
    
    if (hourlyData.count >= MAX_MESSAGES_PER_HOUR) {
      toast({
        title: "Nachrichtenlimit erreicht",
        description: "Sie haben das stündliche Limit erreicht. Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
      return;
    }

    // Update rate limiting state
    setLastMessageTime(now);
    hourlyData.count++;
    sessionStorage.setItem('chatMessageData', JSON.stringify(hourlyData));
    setMessageCount(hourlyData.count);

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: { 
          messages: [...messages, { role: 'user', content: userMessage }]
        }
      });

      if (error) throw error;

      if (data.error) {
        toast({
          title: "Fehler",
          description: data.error,
          variant: "destructive"
        });
        return;
      }

      setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
      
      // If AI suggests contact, show contact options
      if (data.needsContact) {
        setProblemSummary(userMessage);
        setShowAppointmentOptions(false);
      }
      
      // If AI detects appointment request, show appointment options
      if (data.needsAppointment) {
        setShowAppointmentOptions(true);
        setProblemSummary('');
      }

    } catch (error) {
      console.error('Chat error:', error);
      toast({
        title: "Fehler",
        description: "Die Nachricht konnte nicht gesendet werden.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendContactRequest = async () => {
    if (!contactData.name || !contactData.email) {
      toast({
        title: "Fehler",
        description: "Bitte geben Sie mindestens Name und E-Mail ein.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      // Collect all messages for context
      const chatHistory = messages.map(m => `${m.role === 'user' ? 'Kunde' : 'KI'}: ${m.content}`).join('\n');
      
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: contactData.name,
          email: contactData.email,
          phone: contactData.phone || 'Nicht angegeben',
          message: `Problem-Beschreibung: ${problemSummary}\n\n--- Chat-Verlauf ---\n${chatHistory}`,
          service: 'KI-Chat Anfrage'
        }
      });

      if (error) throw error;

      toast({
        title: "Anfrage gesendet!",
        description: "Wir melden uns schnellstmöglich bei Ihnen.",
      });

      setShowContactForm(false);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '✅ Ihre Anfrage wurde erfolgreich gesendet! Wir melden uns in Kürze bei Ihnen. Vielen Dank!'
      }]);

    } catch (error) {
      console.error('Contact error:', error);
      toast({
        title: "Fehler",
        description: "Die Anfrage konnte nicht gesendet werden.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleWidget = () => {
    setIsOpen(!isOpen);
    if (!isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: 'Hallo! 👋 Ich bin der KI-Assistent von Klartech Müritz. Wie kann ich Ihnen heute bei Ihrem IT-Problem helfen?'
      }]);
    }
  };

  return (
    <>
      {/* Chat Widget Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={toggleWidget}
          className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:shadow-2xl text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
          aria-label="KI-Chat öffnen"
        >
          {isOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <MessageCircle className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Chat Widget Popup */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-40 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl border overflow-hidden flex flex-col" style={{ maxHeight: '70vh' }}>
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white p-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold">Klartech KI-Assistent</h3>
                  <p className="text-sm text-cyan-100">IT-Support Beratung</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Chat schließen"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px]">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-r from-cyan-600 to-teal-600 text-white rounded-br-md'
                      : 'bg-gray-100 text-gray-800 rounded-bl-md'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 p-3 rounded-2xl rounded-bl-md">
                  <Loader2 className="h-5 w-5 animate-spin text-cyan-600" />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Appointment Options (shown when AI detects appointment request) */}
          {showAppointmentOptions && !showContactForm && (
            <div className="p-3 bg-teal-50 border-t border-teal-200 flex-shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-teal-600" />
                <p className="text-sm text-teal-800 font-medium">Termin vereinbaren</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={openWhatsApp}
                  className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-2 px-3 rounded-lg text-sm hover:bg-green-700 transition-colors"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </button>
                <a
                  href="tel:+4915128958006"
                  className="flex-1 flex items-center justify-center gap-2 bg-teal-600 text-white py-2 px-3 rounded-lg text-sm hover:bg-teal-700 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  Anrufen
                </a>
              </div>
              <button
                onClick={() => setShowAppointmentOptions(false)}
                className="w-full mt-2 text-xs text-teal-600 hover:underline"
              >
                Schließen
              </button>
            </div>
          )}

          {/* Contact Options (shown when AI suggests contact) */}
          {problemSummary && !showContactForm && !showAppointmentOptions && (
            <div className="p-3 bg-amber-50 border-t border-amber-200 flex-shrink-0">
              <p className="text-sm text-amber-800 mb-2 font-medium">Wie möchten Sie uns kontaktieren?</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowContactForm(true)}
                  className="flex-1 flex items-center justify-center gap-2 bg-cyan-600 text-white py-2 px-3 rounded-lg text-sm hover:bg-cyan-700 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  E-Mail senden
                </button>
                <a
                  href="tel:+4915128958006"
                  className="flex-1 flex items-center justify-center gap-2 bg-teal-600 text-white py-2 px-3 rounded-lg text-sm hover:bg-teal-700 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  Anrufen
                </a>
              </div>
            </div>
          )}

          {/* Contact Form */}
          {showContactForm && (
            <div className="p-4 bg-gray-50 border-t flex-shrink-0">
              <h4 className="font-semibold text-gray-800 mb-3">Ihre Kontaktdaten</h4>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Ihr Name *"
                  value={contactData.name}
                  onChange={(e) => setContactData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
                <input
                  type="email"
                  placeholder="Ihre E-Mail *"
                  value={contactData.email}
                  onChange={(e) => setContactData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
                <input
                  type="tel"
                  placeholder="Telefon (optional)"
                  value={contactData.phone}
                  onChange={(e) => setContactData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setShowContactForm(false)}
                    className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={sendContactRequest}
                    disabled={isLoading}
                    className="flex-1 py-2 bg-cyan-600 text-white rounded-lg text-sm hover:bg-cyan-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Senden
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Input */}
          {!showContactForm && (
            <div className="p-3 border-t flex-shrink-0">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Beschreiben Sie Ihr Problem..."
                  className="flex-1 px-4 py-2 border rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  disabled={isLoading}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || isLoading}
                  className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white p-2 rounded-full hover:shadow-lg transition-all disabled:opacity-50"
                >
                  <Send className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
